import React, { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

const DocumentCollectionApiContext = createContext(null);

export function DocumentCollectionApiProvider({ children }) {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_DOCUMENT);

  const getDocumentCollections = useCallback(
    async (skip = 0, limit = 10) => {
      try {
        const response = await get(`/v1/document_collection?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching document collections:', error);
        throw error;
      }
    },
    [get],
  );

  const getDocuments = useCallback(
    async (skip = 0, limit = 100) => {
      try {
        const response = await get(`/v1/documents?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching document:', error);
        throw error;
      }
    },
    [get],
  );

  const getDocumentCollectionById = useCallback(
    async (collectionId) => {
      try {
        const response = await get(`/v1/document_collection/${collectionId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching document collection by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createDocumentCollection = useCallback(
    async (collection) => {
      try {
        const response = await post(`/v1/document_collection`, collection);
        return response.data;
      } catch (error) {
        console.error('Error creating document collection:', error);
        throw error;
      }
    },
    [post],
  );

  const updateDocumentCollectionById = useCallback(
    async (collectionId, collection) => {
      try {
        const response = await put(`/v1/document_collection/${collectionId}`, collection);
        return response.data;
      } catch (error) {
        console.error('Error updating document collection:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteDocumentCollection = useCallback(
    async (collectionId) => {
      try {
        const response = await del(`/v1/document_collection/${collectionId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting document collection:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <DocumentCollectionApiContext.Provider
      value={{
        getDocumentCollections,
        getDocumentCollectionById,
        createDocumentCollection,
        updateDocumentCollectionById,
        deleteDocumentCollection,
        getDocuments,
      }}
    >
      {children}
    </DocumentCollectionApiContext.Provider>
  );
}

export function useDocumentCollectionApi() {
  const context = useContext(DocumentCollectionApiContext);
  if (!context) {
    throw new Error('useDocumentCollectionApi must be used within a DocumentCollectionApiProvider');
  }
  return context;
}
